import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Alert, FlexColumn, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {ReportToolbar} from './ReportToolbar';
import {EmptyReportState} from './EmptyReportState';
import {ReportErrorState} from './ReportErrorState';
import {LeaveBuilderPrompt} from './LeaveBuilderPrompt';
import {ReportTable} from './ReportTable/ReportTable';
import {TestHook} from './TestHook';
import {useClientGrouping} from './ReportTable/useClientGrouping';
import {AIBanner} from './TopBar/AIBanner';
import {EditTopBar} from './TopBar/EditTopBar';
import {PublishStateBanner} from './TopBar/PublishStateBanner';
import {ReportPageHeader} from './TopBar/ReportPageHeader';
import {ViewTopBar} from './TopBar/ViewTopBar';

const tokens = tokensForThemeProvider.sky.sys;

/** Horizontal page gutter (32px in the design's 1920px frame) and vertical rhythm so the page header,
 * controls row, AI banner, and table are all inset from the viewport edges and evenly spaced,
 * rather than full-bleed — see the design. the design's `Page Header`
 * container has asymmetric vertical padding
 * (`paddingTop=8 paddingBottom=16`, not a uniform 16) and `itemSpacing=16` between its children
 * — not the 12px gap previously used here. */
/* DS FlexColumn defaults to flex:1, which would stretch this to fill the full page height (its
 * parent `Surface` fills the viewport); any leftover height would then be redistributed as growth
 * among children that are themselves FlexColumn/FlexRow-based (the header, the top bar), pushing
 * them apart by an amount that shrinks as the table grows with more rows. Pin to content size so
 * the page's vertical rhythm stays fixed regardless of row count. */
const SurfaceWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['400']};
	width: 100%;
	padding: ${tokens.spacing['200']} ${tokens.spacing['800']} ${tokens.spacing['400']};
	flex: 0 0 auto;
`;

/** Assembled AI Report Builder surface body: the page header (Reports eyebrow + report name
 * heading/pencil), then the mode/role-dependent top bar, then `PublishStateBanner` — telling the
 * editor whether readers are actually isolated from in-progress edits, or telling a reader when
 * the report was last published — then AIBanner — shown only when the report's
 * origin is AI-generated, since showing it for a report opened from anywhere else would claim an
 * origin that isn't true (per the design, the banner otherwise sits BELOW the name/period/Save-Report
 * row, not above it) — then
 * the merged conditions/Group By/Columns `ReportToolbar` strip (edit mode only — view mode goes
 * straight from the top bar to the table, as the design renders it), and the grouped `ReportTable` — or, when the fetch
 * matched zero rows, `EmptyReportState` in its place, explaining the run and naming the active
 * conditions instead of a bare empty grid, or `ReportErrorState` when the fetch failed outright
 * (which is not the same thing as matching nothing). This
 * is the surface BODY only — no modal chrome (see Task 9). Row data and its grouping/table state
 * all come from `useReportBuilder()`; grouping is applied client-side via `useClientGrouping`
 * before handing rows to `ReportTable`. Mode switching is done solely via the "Save Report"
 * (edit→view) and "Edit Report" (view→edit) buttons in the top bars — no mode-toggle UI here. */
export function ReportBuilderSurface(): JSX.Element {
	const {
		definition,
		mode,
		source,
		tableState,
		updateTableState,
		toggleFavorite,
		rows,
		totalCount,
		cappedTotalEstimate,
		totalIsFloor,
		capped,
		grouping,
		hasFetched,
		fetchFailed,
		retryFetch,
	} = useReportBuilder();
	const {formatMessage} = useIntl();
	const [capBannerDismissed, setCapBannerDismissed] = useState(false);

	// Dismissal applies to the result on screen, not to the report forever. Editing the report
	// re-fetches (a new `definition` identity is what drives the fetch), and that new result can be
	// capped too — so a dismissal that survived would silently hide the truncation.
	useEffect(() => {
		setCapBannerDismissed(false);
	}, [definition]);

	const groupedRows = useClientGrouping(rows, grouping);

	const totalLabel = totalIsFloor ? `${totalCount}+` : String(totalCount);
	const capBanner =
		capped && !capBannerDismissed ? (
			<Alert
				type={Alert.TYPE.INFORMATION}
				text={formatMessage({id: 'ai_report_builder.row_cap_banner'}, {shown: cappedTotalEstimate, total: totalLabel})}
				onDismiss={() => setCapBannerDismissed(true)}
			/>
		) : null;

	return (
		<TestHook data-cy={'ai-report-builder-surface'}>
			<SurfaceWrapper>
				<LeaveBuilderPrompt />

				<ReportPageHeader />

				{mode === 'edit' ? <EditTopBar /> : <ViewTopBar />}

				<PublishStateBanner />

				{source === 'AI' ? <AIBanner /> : null}

				{mode === 'edit' ? <ReportToolbar /> : null}

				{capBanner}

				{fetchFailed ? (
					<ReportErrorState onRetry={retryFetch} />
				) : hasFetched && rows.length === 0 ? (
					<EmptyReportState definition={definition} mode={mode} />
				) : (
					<ReportTable
						rows={groupedRows}
						definition={definition}
						tableState={tableState}
						onTableStateChange={updateTableState}
						onToggleFavorite={toggleFavorite}
					/>
				)}
			</SurfaceWrapper>
		</TestHook>
	);
}
